import { useTranslation } from 'react-i18next'

import { DatePicker, Field, Select } from '@/components/ui/Field'
import { toOptions, type ChoiceOption } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { refusalMessage } from '@/features/cases/lib/refusal'
import {
  useIssuingEntityOptions,
  usePayCarrier,
  type CarrierPaymentPayload,
} from '@/features/finance/api/useFinance'
import { CounterpartySelect } from '@/features/finance/components/CounterpartySelect'
import { normaliseDecimal } from '@/features/finance/lines'
import { formatMoney } from '@/lib/format'
import type {
  ExpenseLine,
  LegVehicle,
  Maybe,
  Money,
  PaymentMethod,
  VehicleMoney,
} from '@/types/domain'

/* «Оплатить перевозчику» — one form for what used to be three acts.
 *
 * ── What it replaced, and why one sheet rather than two ─────────────────────
 *
 * Marking "we paid 3 000 for truck X" took three separate writes: record the
 * ACTUAL cost line (because a plan is not a debt and nothing can be allocated
 * against it), record the payment, then allocate the payment to the line. Three
 * windows, and the chain broke in two places — the carrier often sends no
 * invoice at all, so there was no actual line to point at; and each write could
 * land while the next was refused, leaving a booked payment attached to nothing.
 *
 * So this sheet asks its three questions in one column and sends ONE request
 * (spec L5). The server recognises the debt, records the payment and allocates
 * it inside one transaction: all of it, or none of it. There is one refusal to
 * read and one draft to keep when it comes.
 *
 * ── The client decides which shape the body takes, and that is deliberate ────
 *
 * The body carries EITHER an `expenseLineId` — the truck's cost is already a
 * fact and this money settles it — OR a `debt` block naming the plan to
 * realise. Exactly one, and the server refuses both or neither
 * (`ErrCarrierDebtOrLine`). This side chooses because this side is the one that
 * knows: it is holding the leg's money block, with the agreed rate and the
 * recorded actual side by side per truck. A server left to infer it would be
 * guessing at what the person looking at the screen can simply see.
 *
 * ── The debt defaults to the rate, and the excess needs a reason (L2) ────────
 *
 * The agreed rate IS the deal on most of these trucks, so it is what the debt
 * field opens with — editable, because an invoice that differs is ordinary. What
 * is not ordinary is paying MORE than was agreed with no explanation: above the
 * plan the reason field becomes required, here as well as on the server. It goes
 * into `remarks`, which is AD-6-suppressed, and the variance shows up on the
 * money table as a plan/actual gap rather than being silently absorbed.
 *
 * ── What this sheet never states ────────────────────────────────────────────
 *
 * No booking (AD-9/AD-20 — the server resolves the rate and computes the USD),
 * no allocation figures (AD-11), no direction, no case id and NO STATUS. The
 * status follows the recorder's capability server-side (L3): a logist's record
 * is born `pending` and waits for Finance. A client that named one would be
 * granting itself the confirmation it is supposed to be waiting for.
 */

/** The server's own spellings, keyed to match `finance.payments.methods.*`. */
const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** Accepts `1234.56`, `1 234,56` and `1,234.56`; rejects text and blanks.
 *
 *  The same four-line check the payment and allocation sheets carry, repeated
 *  rather than shared for the reason `PaymentAllocationSheet` states: a
 *  component file may export only components (react-refresh), and a regex is a
 *  poorer reason to add a module than the confusion of importing form
 *  validation out of a file full of JSX. */
function isValidAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** What one truck's cost looks like right now, out of the leg's money block.
 *
 *  `actualId` is the whole of the distinction and it is the same one
 *  `LegFinance` splits its rows on: a folded line carrying one is a DEBT — a
 *  cost somebody has recognised, and the only thing an allocation may target —
 *  while one without is still the agreed rate and nothing more.
 *
 *  An UNPLANNED actual carries an `actualId` too, so it lands in `actuals`
 *  rather than being mistaken for a plan: it is a real cost with no rate behind
 *  it, which is precisely a debt this payment can settle. */
function costsFor(expenses: ExpenseLine[], vehicleId: string) {
  const mine = expenses.filter((line) => line.vehicleId === vehicleId)
  return {
    actuals: mine.filter((line) => line.actualId !== undefined),
    /** The agreed rate awaiting realisation — at most one per truck in practice,
     *  and the first is the one the debt is derived from. */
    plan: mine.find((line) => line.actualId === undefined),
  }
}

/** What a line is worth: the recorded figure, falling back to the agreed one. */
function figureOf(line: ExpenseLine): Money {
  return line.actual ?? line.planned
}

interface CarrierPaymentDraft {
  vehicleId: string
  /** The ACTUAL line this money settles, or '' when the debt is being
   *  recognised in the same request. Never both — see the file header. */
  expenseLineId: string
  debtAmount: string
  debtComment: string
  reference: string
  counterpartyName: string
  method: PaymentMethod
  issuingEntityId: string
  amount: string
  currency: string
  feeAmount: string
  feeCurrency: string
  receivedOn: string
}

/** The truck-dependent half of the draft, recomputed whenever the truck changes.
 *
 *  A single actual IS preselected, and that is not the guess AD-11 forbids: the
 *  no-guess rule is about which paper a payment settles when several could, and
 *  a truck with exactly one recorded cost offers no choice to make. Two or more
 *  and nothing is chosen — that one is a decision, and it is asked for.
 *
 *  The CURRENCY travels with the truck for the same reason the debt figure
 *  does: it is the denomination the rate was agreed in, not a number anybody is
 *  inventing.
 *
 *  The COUNTERPARTY is the truck's own haulier (0055 subcontracts per truck),
 *  falling back to the direction's when the row names none — the row the
 *  reader pressed Pay on already prints who is owed. Absent-or-hidden falls
 *  through to '': a marker this reader may not see must not look answered
 *  (AD-6).
 *
 *  The payment's AMOUNT is seeded with the truck's own figure — but only while
 *  NOTHING has been paid against the truck yet. Not a guess: the figure is the
 *  debt the reader is looking at, and «заплатили по ставке» is the ordinary
 *  act this form exists for; an advance is typed over it. Once something HAS
 *  been paid the box opens blank, because the honest seed would be the
 *  remainder, and the remainder is cross-currency arithmetic this client never
 *  performs — the rollup's `paid` is USD at booked rates while the rate is in
 *  its own currency, and nothing on these screens adds two decimal strings. */
function seedFor(
  expenses: ExpenseLine[],
  vehicleId: string,
  context: {
    vehicles: LegVehicle[]
    perVehicle: VehicleMoney[]
    legCarrier?: Maybe<string>
  },
) {
  const { actuals, plan } = costsFor(expenses, vehicleId)
  const only = actuals.length === 1 ? actuals[0] : undefined
  const denomination = only ? figureOf(only) : plan?.planned

  const truckCarrier = context.vehicles.find(
    (vehicle) => vehicle.id === vehicleId,
  )?.carrierName
  const counterpartyName =
    typeof truckCarrier === 'string' && truckCarrier !== ''
      ? truckCarrier
      : typeof context.legCarrier === 'string'
        ? context.legCarrier
        : ''

  const paidSoFar = context.perVehicle.find(
    (row) => row.vehicleId === vehicleId,
  )?.paid
  const untouched = paidSoFar === undefined || Number(paidSoFar.amount) === 0

  return {
    expenseLineId: only?.actualId ?? '',
    debtAmount: plan ? plan.planned.amount : '',
    debtComment: '',
    currency: denomination?.currency ?? 'USD',
    counterpartyName,
    amount: vehicleId !== '' && untouched && denomination ? denomination.amount : '',
  }
}

export function CarrierPaymentSheet({
  open,
  onClose,
  caseId,
  legId,
  vehicles,
  expenses,
  /** The truck the sheet opens on, when it was opened from that truck's row.
   *  Absent from the card's own header button, where no truck is implied. */
  defaultVehicleId,
  /** The haulier named on the direction — the FALLBACK counterparty, behind
   *  the truck's own (`seedFor`). A `Maybe` because AD-6 withholds it from a
   *  reader who may not see who carries the cargo — in which case the
   *  counterparty box opens EMPTY rather than seeded from a marker they cannot
   *  read, which would look answered. */
  carrierName,
  /** The per-truck rollup off the leg's money block, for the one question the
   *  amount seed asks of it: has anything been paid against this truck yet.
   *  Absent reads as "nothing paid", which is what a truck with no rollup row
   *  means on the card too. */
  perVehicle,
}: {
  open: boolean
  onClose: () => void
  caseId: string
  legId: string
  vehicles: LegVehicle[]
  /** The leg's own cost lines, folded plan-beside-actual — the same rows the
   *  card above renders. Passed in rather than read again: the caller is
   *  holding them, and a second read would be a second answer to what this
   *  truck's rate is. */
  expenses: ExpenseLine[]
  defaultVehicleId?: string
  carrierName?: Maybe<string>
  perVehicle?: VehicleMoney[]
}) {
  const { t } = useTranslation()
  const { data: entities } = useIssuingEntityOptions()
  const pay = usePayCarrier()

  const [draft, setDraft, touched, markTouched, dirty] =
    useFormDraft<CarrierPaymentDraft>(
      // Keyed on the truck as well as the leg, so opening from a different
      // row re-derives the debt rather than carrying the last truck's rate
      // across into a form about a different one.
      open ? `${legId}:${defaultVehicleId ?? ''}` : null,
      () => {
        const vehicleId = defaultVehicleId ?? ''
        /* Seeded and then editable, never applied on submit — the rule
           `PaymentFormSheet.defaults` states: what the reader can see and
           correct is what gets written. The counterparty and the amount are
           part of the truck's seed now (`seedFor`), so a row's Pay button
           opens a form already carrying what the row already showed. */
        const seeded = seedFor(expenses, vehicleId, {
          vehicles,
          perVehicle: perVehicle ?? [],
          legCarrier: carrierName,
        })
        return {
          vehicleId,
          ...seeded,
          reference: '',
          method: 'bank_transfer' as PaymentMethod,
          issuingEntityId: '',
          feeAmount: '',
          // '' MEANS "the payment's own", which is what the control renders and
          // what the payload resolves. A code seeded here would pin the fee the
          // moment the payment currency was changed.
          feeCurrency: '',
          receivedOn: today(),
        }
      },
    )

  const { actuals, plan } = costsFor(expenses, draft.vehicleId)
  /* Which of the two bodies this form is building. A truck with any recorded
     cost settles that cost; only a truck with none recognises a debt. */
  const settlingExisting = actuals.length > 0
  /* The debt is denominated in the rate's currency when there is a rate, and in
     the payment's own when the cost is unplanned — there is nothing else to
     take it from, and two controls for one denomination is a pair that can
     disagree. */
  const debtCurrency = plan ? plan.planned.currency : draft.currency

  /* The plan the debt is being measured against, and the rule that makes the
     reason mandatory (L2). Only ABOVE the plan: paying less than agreed is an
     ordinary part payment, and demanding a justification for it would train
     people to type "ok" into a field that exists to carry a real one. */
  const overPlan =
    plan !== undefined &&
    isValidAmount(draft.debtAmount) &&
    Number(normaliseDecimal(draft.debtAmount)) > Number(plan.planned.amount)
  const commentRequired = overPlan && draft.debtComment.trim() === ''

  const vehicleError =
    touched && draft.vehicleId === '' ? t('common.form.required') : undefined
  const lineError =
    touched && settlingExisting && draft.expenseLineId === ''
      ? t('common.form.required')
      : undefined
  const debtError =
    touched && !settlingExisting && !isValidAmount(draft.debtAmount)
      ? t('common.form.invalidNumber')
      : undefined
  const commentError = touched && commentRequired ? t('common.form.required') : undefined
  const referenceError =
    touched && !draft.reference.trim() ? t('common.form.required') : undefined
  const counterpartyError =
    touched && !draft.counterpartyName.trim() ? t('common.form.required') : undefined
  const entityError =
    touched && !draft.issuingEntityId ? t('common.form.required') : undefined
  const amountError =
    touched && !isValidAmount(draft.amount) ? t('common.form.invalidNumber') : undefined
  /* The fee is OPTIONAL, so a blank box is correct and only a filled-in one is
     checked — the server refuses only a half-answered pair, per field. */
  const feeTyped = draft.feeAmount.trim() !== ''
  const feeError =
    touched && feeTyped && !isValidAmount(draft.feeAmount)
      ? t('common.form.invalidNumber')
      : undefined

  const invalid =
    draft.vehicleId === '' ||
    (settlingExisting ? draft.expenseLineId === '' : !isValidAmount(draft.debtAmount)) ||
    commentRequired ||
    !draft.reference.trim() ||
    !draft.counterpartyName.trim() ||
    !draft.issuingEntityId ||
    !isValidAmount(draft.amount) ||
    (feeTyped && !isValidAmount(draft.feeAmount))

  /* Re-derived WITH the truck, in one setState. Leaving the debt behind when
     the truck changed is how a form ends up sending truck 2's rate under truck
     3's plan id — which the server would refuse, one round trip after the
     mistake stopped being visible on screen. The counterparty and the amount
     re-derive too: a different truck is a different haulier and a different
     figure, and half a form about the previous truck is worse than a reseed
     the reader can still edit. */
  const chooseVehicle = (vehicleId: string) =>
    setDraft({
      ...draft,
      vehicleId,
      ...seedFor(expenses, vehicleId, {
        vehicles,
        perVehicle: perVehicle ?? [],
        legCarrier: carrierName,
      }),
    })

  const vehicleOptions: ChoiceOption[] = vehicles.map((vehicle) => ({
    value: vehicle.id,
    label: vehicle.plate,
    hint: t('cases.legFinance.pay.vehiclePosition', { position: vehicle.position }),
  }))

  const lineOptions: ChoiceOption[] = actuals.map((line) => ({
    value: line.actualId ?? '',
    label: line.description,
    hint: formatMoney(figureOf(line).amount, figureOf(line).currency),
  }))

  const submit = () => {
    markTouched()
    if (invalid) return

    const payment: CarrierPaymentPayload['payment'] = {
      reference: draft.reference.trim(),
      counterpartyName: draft.counterpartyName.trim(),
      method: draft.method,
      issuingEntityId: draft.issuingEntityId,
      amount: {
        // A decimal STRING all the way through (AD-9), normalised only for the
        // separators a Russian keyboard produces — never through Number().
        amount: normaliseDecimal(draft.amount),
        currency: draft.currency,
      },
      /* OMITTED when nothing was typed, never sent as a zero: absent means "no
         fee was recorded", and a 0.00 would read on a report as a transfer that
         cost the company nothing. */
      ...(feeTyped
        ? {
            fee: {
              amount: normaliseDecimal(draft.feeAmount),
              currency: draft.feeCurrency === '' ? draft.currency : draft.feeCurrency,
            },
          }
        : {}),
      receivedOn: draft.receivedOn,
    }

    const payload: CarrierPaymentPayload = settlingExisting
      ? { expenseLineId: draft.expenseLineId, payment }
      : {
          debt: {
            ...(plan ? { planLineId: plan.id } : {}),
            /* Sent on both branches. The server requires it for an UNPLANNED
               line, which has nothing else to attribute it to a truck by, and
               it is checked against the plan's own vehicle on the other — the
               refusal names the mismatch instead of filing the cost against a
               truck nobody chose. */
            vehicleId: draft.vehicleId,
            amount: normaliseDecimal(draft.debtAmount),
            currency: debtCurrency,
            ...(draft.debtComment.trim() === ''
              ? {}
              : { comment: draft.debtComment.trim() }),
          },
          payment,
        }

    // Closed from onSuccess only: a refusal leaves the sheet open holding the
    // whole draft rather than closing over a write that never landed (UX-DR-18).
    pay.mutate({ caseId, legId, payload }, { onSuccess: onClose })
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('cases.legFinance.pay.title')}
      submitLabel={t('cases.legFinance.pay.submit')}
      pending={pay.isPending}
      dirty={dirty}
      disabled={vehicles.length === 0}
      notice={
        vehicles.length === 0
          ? t('cases.legFinance.pay.noVehicles')
          : t('cases.legFinance.pay.atomicNote')
      }
      error={
        pay.error
          ? refusalMessage(pay.error, t)
          : touched && invalid
            ? t('common.form.fixFields')
            : undefined
      }
      onSubmit={submit}
    >
      {/* The truck first: it decides what the whole form below it means — which
          rate is the debt, which recorded cost is being settled, and which
          currency the two are denominated in. */}
      <Field
        label={t('cases.legFinance.pay.vehicle')}
        required
        error={vehicleError}
        hint={t('cases.legFinance.pay.vehicleHint')}
      >
        {(props) => (
          <Select
            value={draft.vehicleId}
            onChange={chooseVehicle}
            options={vehicleOptions}
            placeholder={t('cases.legFinance.pay.vehiclePlaceholder')}
            {...props}
          />
        )}
      </Field>

      <div className="border-line-hair flex flex-col gap-2.5 border-t pt-3">
        <span className="asa-eyebrow">{t('cases.legFinance.pay.debtHeading')}</span>

        {draft.vehicleId === '' ? (
          <p className="text-text-muted text-[12px] leading-snug font-light">
            {t('cases.legFinance.pay.chooseVehicleFirst')}
          </p>
        ) : settlingExisting ? (
          /* The cost is already a fact, so there is nothing to state about it —
             only, when there is more than one, which of them this money
             settles. A single recorded cost is shown and not editable: amending
             it is the money table's act, and offering it here would be a second
             way to change a figure that already exists. */
          actuals.length === 1 ? (
            <p className="text-text-body text-[12.5px] leading-snug">
              {t('cases.legFinance.pay.existingActual', {
                item: actuals[0]!.description,
                amount: formatMoney(
                  figureOf(actuals[0]!).amount,
                  figureOf(actuals[0]!).currency,
                ),
              })}
            </p>
          ) : (
            <Field
              label={t('cases.legFinance.pay.chooseLine')}
              required
              error={lineError}
              hint={t('cases.legFinance.pay.chooseLineHint')}
            >
              {(props) => (
                <Select
                  value={draft.expenseLineId}
                  onChange={(expenseLineId) => setDraft({ ...draft, expenseLineId })}
                  options={lineOptions}
                  placeholder={t('common.form.selectPlaceholder')}
                  {...props}
                />
              )}
            </Field>
          )
        ) : (
          <>
            <Field
              label={t('cases.legFinance.pay.debtAmount')}
              required
              error={debtError}
              hint={
                plan
                  ? t('cases.legFinance.pay.debtFromPlan', {
                      amount: formatMoney(plan.planned.amount, plan.planned.currency),
                    })
                  : t('cases.legFinance.pay.debtUnplanned')
              }
            >
              {(props) => (
                <span className="flex items-center gap-2.5">
                  <Input
                    inputMode="decimal"
                    value={draft.debtAmount}
                    onChange={(e) => setDraft({ ...draft, debtAmount: e.target.value })}
                    className="asa-num"
                    {...props}
                  />
                  {/* Shown, not chosen. The debt is denominated by the rate it
                      realises — or, unplanned, by the payment that settles it. */}
                  <span className="text-text-muted shrink-0 text-[12.5px]">
                    {debtCurrency}
                  </span>
                </span>
              )}
            </Field>

            {/* Required only ABOVE the plan (L2), and required client-side so
                the refusal arrives beside the field rather than after the whole
                draft has been sent. It lands in `remarks`, which AD-6
                suppresses — a reason may name a counterparty. */}
            {plan ? (
              <Field
                label={t('cases.legFinance.pay.debtComment')}
                required={overPlan}
                error={commentError}
                hint={
                  overPlan
                    ? t('cases.legFinance.pay.debtCommentRequired', {
                        amount: formatMoney(
                          plan.planned.amount,
                          plan.planned.currency,
                        ),
                      })
                    : t('cases.legFinance.pay.debtCommentHint')
                }
              >
                {(props) => (
                  <Input
                    value={draft.debtComment}
                    onChange={(e) => setDraft({ ...draft, debtComment: e.target.value })}
                    {...props}
                  />
                )}
              </Field>
            ) : null}
          </>
        )}
      </div>

      <div className="border-line-hair flex flex-col gap-2.5 border-t pt-3">
        <span className="asa-eyebrow">{t('cases.legFinance.pay.paymentHeading')}</span>

        <Field
          label={t('finance.payments.reference')}
          required
          error={referenceError}
          hint={t('finance.payments.referenceHint')}
        >
          {(props) => (
            <Input
              value={draft.reference}
              onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
              className="font-mono"
              {...props}
            />
          )}
        </Field>

        {/* PICKED, not typed. What is submitted is still the NAME — there is no
            counterparty id on a payment — and what the picker buys is spelling:
            balances are keyed by this string, so two spellings of one haulier
            are two half-right balances. */}
        <Field
          label={t('finance.balances.counterparty')}
          required
          error={counterpartyError}
          hint={t('finance.payments.counterpartyHint')}
        >
          {(props) => (
            <CounterpartySelect
              value={draft.counterpartyName}
              onChange={(counterpartyName) => setDraft({ ...draft, counterpartyName })}
              {...props}
            />
          )}
        </Field>

        <div className="flex gap-2.5">
          <Field
            label={t('common.fields.amount')}
            required
            error={amountError}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field label={t('finance.payments.currency')} className="w-28 shrink-0">
            {(props) => (
              <Select
                value={draft.currency}
                onChange={(currency) => setDraft({ ...draft, currency })}
                // A currency code is already its own label.
                options={toOptions(CURRENCIES, (currency) => currency)}
                {...props}
              />
            )}
          </Field>
        </div>

        <div className="flex gap-2.5">
          {/* Cash or bank. A fact about the payment rather than a preference: a
              cash handover at a border post and a SWIFT transfer reconcile
              against different evidence. */}
          <Field label={t('finance.payments.method')} required className="flex-1">
            {(props) => (
              <Select
                value={draft.method}
                onChange={(method) => setDraft({ ...draft, method })}
                options={toOptions(METHODS, (method) =>
                  t(`finance.payments.methods.${method}`),
                )}
                {...props}
              />
            )}
          </Field>

          {/* OURS, not theirs: which of the company's entities the money left,
              which is what netting is computed per (FR-35). */}
          <Field
            label={t('finance.payments.entity')}
            required
            error={entityError}
            className="flex-1"
          >
            {(props) => (
              <Select
                value={draft.issuingEntityId}
                onChange={(issuingEntityId) => setDraft({ ...draft, issuingEntityId })}
                options={(entities ?? []).map((entity) => ({
                  value: entity.id,
                  label: entity.name,
                }))}
                placeholder={t('common.form.selectPlaceholder')}
                {...props}
              />
            )}
          </Field>
        </div>

        <div className="flex gap-2.5">
          <Field
            label={t('finance.payments.fee')}
            error={feeError}
            hint={t('finance.payments.feeHint')}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.feeAmount}
                onChange={(e) => setDraft({ ...draft, feeAmount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field label={t('finance.payments.feeCurrency')} className="w-28 shrink-0">
            {(props) => (
              <Select
                value={draft.feeCurrency === '' ? draft.currency : draft.feeCurrency}
                onChange={(feeCurrency) => setDraft({ ...draft, feeCurrency })}
                options={toOptions(CURRENCIES, (currency) => currency)}
                {...props}
              />
            )}
          </Field>
        </div>

        {/* Money cannot leave in the future: a date past today is always a typo,
            and the picker refuses it rather than booking it. */}
        <Field label={t('finance.payments.received')} required>
          {(props) => (
            <DatePicker
              value={draft.receivedOn}
              max={today()}
              onChange={(receivedOn) => setDraft({ ...draft, receivedOn })}
              {...props}
            />
          )}
        </Field>
      </div>
    </FormSheet>
  )
}
